import api from './api';
import appDataService from './appDataService';
import tournamentService, { Tournament } from './tournamentService';

// ============================================
// Globale Suche (Port des Flutter SearchScreens /suchen)
//
// Flutter sucht client-seitig über die bereits geladenen Listen
// (Turniere, Vereine, Spielstätten, Shops, Serien).
// Es gibt KEINEN globalen Backend-Search-Endpunkt – daher:
// - Turniere:      GET /api/tournaments (client-seitig gefiltert)
// - Vereine/Spielstätten/Shops/Serien: GET /api/appdata (client-seitig gefiltert)
// - News:          GET /api/news-articles/search?q=...
// - Forum:         GET /api/forum/search?query=...&type=topics
//
// WICHTIG: Alles defensiv parsen – Felder können fehlen oder null sein!
// ============================================

export type SearchResultType =
  | 'tournament'
  | 'club'
  | 'pub'
  | 'shop'
  | 'series'
  | 'news'
  | 'forum';

export interface SearchResult {
  type: SearchResultType;
  id: string;
  title: string;
  subtitle?: string | null;
  imageUrl?: string | null;
  /** Ziel-Link zur Detail-Seite (null, wenn kein Slug vorhanden) */
  link?: string | null;
}

export interface GroupedSearchResults {
  tournament: SearchResult[];
  club: SearchResult[];
  pub: SearchResult[];
  shop: SearchResult[];
  series: SearchResult[];
  news: SearchResult[];
  forum: SearchResult[];
}

export const SEARCH_TYPE_LABELS: Record<SearchResultType, string> = {
  tournament: 'Turniere',
  club: 'Vereine',
  pub: 'Spielstätten',
  shop: 'Shops',
  series: 'Turnierserien',
  news: 'News',
  forum: 'Forum',
};

export const SEARCH_TYPE_ORDER: SearchResultType[] = [
  'tournament',
  'club',
  'pub',
  'shop',
  'series',
  'news',
  'forum',
];

/** Maximale Treffer pro Typ */
const MAX_RESULTS_PER_TYPE = 20;

// ============================================
// Helper
// ============================================

const normalize = (value?: string | null): string =>
  (value || '').toLowerCase().trim();

/** Prüft, ob eines der Felder den Suchbegriff enthält (case-insensitive). */
const matchesQuery = (query: string, ...fields: (string | null | undefined)[]): boolean => {
  const q = normalize(query);
  if (!q) return false;
  return fields.some((f) => normalize(f).includes(q));
};

const safeString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

/** Datum formatieren wie in der Flutter-App (Heute / Morgen / dd.MM.yyyy). */
const formatDate = (timestamp?: number | null): string => {
  if (!timestamp || typeof timestamp !== 'number' || isNaN(timestamp)) return '';
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

  if (dateOnly === today) return 'Heute';
  if (dateOnly === today + 24 * 60 * 60 * 1000) return 'Morgen';
  return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
    .toString()
    .padStart(2, '0')}.${date.getFullYear()}`;
};

/** Datums-Range wie in Flutter (_formatDateRange). */
const formatDateRange = (dates?: number[] | null): string => {
  if (!dates || !Array.isArray(dates) || dates.length === 0) return '';
  const valid = dates.filter((d) => typeof d === 'number' && !isNaN(d));
  if (valid.length === 0) return '';
  const sorted = [...valid].sort((a, b) => a - b);
  const first = formatDate(sorted[0]);
  const last = formatDate(sorted[sorted.length - 1]);
  if (!first) return '';
  if (sorted.length === 1 || first === last) return first;
  return `${first} - ${last}`;
};

// ============================================
// Einzelne Such-Quellen (alle fehlertolerant)
// ============================================

async function searchTournaments(query: string): Promise<SearchResult[]> {
  try {
    const response = await tournamentService.getTournaments();
    const tournaments: Tournament[] = Array.isArray(response?.data) ? response.data : [];

    return tournaments
      .filter((t) =>
        matchesQuery(
          query,
          t?.name,
          t?.address?.label,
          t?.address?.city,
          t?.description,
        ),
      )
      .slice(0, MAX_RESULTS_PER_TYPE)
      .map((t) => {
        const dateRange = formatDateRange(t?.dates);
        const addressLabel = safeString(t?.address?.label);
        const subtitle = [addressLabel, dateRange].filter(Boolean).join(' • ') || null;
        const slug = safeString(t?.slug);
        return {
          type: 'tournament' as const,
          id: t?.id || t?._id || '',
          title: safeString(t?.name) || 'Unbenanntes Turnier',
          subtitle,
          imageUrl: tournamentService.getThumbnailUrl(t, 200),
          link: slug ? `/turnier/${slug}` : null,
        };
      })
      .filter((r) => r.id);
  } catch (error) {
    console.error('Suche (Turniere) fehlgeschlagen:', error);
    return [];
  }
}

type AppDataKind = 'club' | 'pub' | 'shop' | 'series';

async function searchAppDataItems(
  query: string,
  kind: AppDataKind,
): Promise<SearchResult[]> {
  try {
    let items: any[] = [];
    let fallbackSubtitle = '';
    let linkPrefix = '';

    switch (kind) {
      case 'club':
        items = await appDataService.getClubs();
        fallbackSubtitle = 'Verein';
        linkPrefix = '/verein';
        break;
      case 'pub':
        items = await appDataService.getPubs();
        fallbackSubtitle = 'Spielstätte';
        linkPrefix = '/spielstaette';
        break;
      case 'shop':
        items = await appDataService.getShops();
        fallbackSubtitle = 'Dart-Shop';
        linkPrefix = '/shop';
        break;
      case 'series':
        items = await appDataService.getSeries();
        fallbackSubtitle = 'Turnierserie';
        linkPrefix = '/turnierserie';
        break;
    }

    if (!Array.isArray(items)) return [];

    return items
      .filter((item) =>
        matchesQuery(
          query,
          item?.name,
          item?.address?.label,
          item?.address?.city,
          item?.description,
        ),
      )
      .slice(0, MAX_RESULTS_PER_TYPE)
      .map((item) => {
        const slug = safeString(item?.slug);
        return {
          type: kind as SearchResultType,
          id: item?._id || item?.id || '',
          title: safeString(item?.name) || 'Kein Name',
          subtitle: safeString(item?.address?.label) || fallbackSubtitle,
          imageUrl: appDataService.getImageUrl(item),
          link: slug ? `${linkPrefix}/${slug}` : null,
        };
      })
      .filter((r) => r.id);
  } catch (error) {
    console.error(`Suche (${kind}) fehlgeschlagen:`, error);
    return [];
  }
}

async function searchNews(query: string): Promise<SearchResult[]> {
  try {
    const response = await api.get('/api/news-articles/search', {
      params: { q: query, limit: MAX_RESULTS_PER_TYPE, offset: 0 },
    });

    // Backend liefert { articles: [...], pagination: {...} } – defensiv parsen
    const raw = response?.data;
    const articles: any[] = Array.isArray(raw?.articles)
      ? raw.articles
      : Array.isArray(raw)
        ? raw
        : [];

    return articles
      .map((a) => {
        const slug = safeString(a?.slug);
        const image = safeString(a?.imageUrl) || safeString(a?.image) || safeString(a?.thumbnail);
        let imageUrl: string | null = null;
        if (image) {
          imageUrl = image.startsWith('http')
            ? image
            : `${api.defaults.baseURL || ''}${image.startsWith('/') ? '' : '/'}${image}`;
        }
        return {
          type: 'news' as const,
          id: a?._id || a?.id || slug || '',
          title: safeString(a?.title) || 'Ohne Titel',
          subtitle: safeString(a?.subtitle) || safeString(a?.source) || 'News',
          imageUrl,
          link: slug ? `/news/${slug}` : null,
        };
      })
      .filter((r) => r.id);
  } catch (error) {
    console.error('Suche (News) fehlgeschlagen:', error);
    return [];
  }
}

async function searchForum(query: string): Promise<SearchResult[]> {
  try {
    // Backend-Validation: query min. 2 Zeichen, Pflichtfeld
    const response = await api.get('/api/forum/search', {
      params: { query, type: 'topics', limit: MAX_RESULTS_PER_TYPE, page: 1 },
    });

    // Backend liefert { topics: [...], posts: [...] } – defensiv parsen
    const raw = response?.data;
    const topics: any[] = Array.isArray(raw?.topics)
      ? raw.topics
      : Array.isArray(raw)
        ? raw
        : [];

    return topics
      .map((t) => {
        const slug = safeString(t?.slug);
        const subtitleParts = [
          safeString(t?.categoryName),
          safeString(t?.authorName) ? `von ${safeString(t?.authorName)}` : null,
        ].filter(Boolean);
        return {
          type: 'forum' as const,
          id: t?._id || t?.id || slug || '',
          title: safeString(t?.title) || 'Ohne Titel',
          subtitle: subtitleParts.length > 0 ? subtitleParts.join(' • ') : 'Forum-Topic',
          imageUrl: safeString(t?.headerImageUrl),
          link: slug ? `/forum/topic/${slug}` : null,
        };
      })
      .filter((r) => r.id);
  } catch (error) {
    console.error('Suche (Forum) fehlgeschlagen:', error);
    return [];
  }
}

// ============================================
// Öffentliche API
// ============================================

const emptyResults = (): GroupedSearchResults => ({
  tournament: [],
  club: [],
  pub: [],
  shop: [],
  series: [],
  news: [],
  forum: [],
});

class SearchService {
  /** Mindestlänge des Suchbegriffs (wie Forum-Backend-Validation). */
  readonly minQueryLength = 2;

  /**
   * Globale Suche über alle Entitätstypen.
   * Alle Quellen laufen parallel; einzelne Fehler führen NICHT zum Gesamtfehler.
   */
  async searchAll(query: string): Promise<GroupedSearchResults> {
    const trimmed = (query || '').trim();
    if (trimmed.length < this.minQueryLength) {
      return emptyResults();
    }

    const [tournament, club, pub, shop, series, news, forum] = await Promise.all([
      searchTournaments(trimmed),
      searchAppDataItems(trimmed, 'club'),
      searchAppDataItems(trimmed, 'pub'),
      searchAppDataItems(trimmed, 'shop'),
      searchAppDataItems(trimmed, 'series'),
      searchNews(trimmed),
      searchForum(trimmed),
    ]);

    return { tournament, club, pub, shop, series, news, forum };
  }

  /** Gesamtanzahl aller Treffer. */
  countResults(results?: GroupedSearchResults | null): number {
    if (!results) return 0;
    return SEARCH_TYPE_ORDER.reduce(
      (sum, type) => sum + (Array.isArray(results[type]) ? results[type].length : 0),
      0,
    );
  }

  /** Leeres Ergebnis-Objekt (z. B. für Initial-State). */
  emptyResults(): GroupedSearchResults {
    return emptyResults();
  }
}

export default new SearchService();
